import React from 'react';
import styled, { css } from 'styled-components';
import { Classable, EntityWithCoordinates, HasChildren, Shapeable, Slice } from '@shapeable/types';
import { breakpoints, theme, TRANSITION } from '@shapeable/theme';
import { classNames, entityTypeNameFor, locationDMS } from '@shapeable/utils';
import { AsideMapButton, AsideSection, CellLabel, ContentNode, IconLabelLink, useLang, usePageEntity } from '@shapeable/ui';
import { useMapUtils } from '@shapeable/maps';
import { AUSTRALIA_MAP_THUMBNAIL } from '../../data';

const cls = classNames('slice-aside-map-link');

// -------- Types -------->

// -------- Props -------->

export type SliceAsideMapLinkProps = Classable & HasChildren & { 
  entity?: Slice;
  zoom?: number;
};

export const SliceAsideMapLinkDefaultProps: Omit<SliceAsideMapLinkProps, 'entity'> = {
  zoom: 6,
};

// -------- Child Component Props -------->

type ContainerProps = {

};

// -------- Styles -------->

const ContainerStyles = breakpoints({
  base: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.UNIT(6)};
    padding-top: ${theme.UNIT(4)};
    margin-bottom: ${theme.UNIT(3)};
  `,
  desktop: css`
    margin-bottom: 0;
  `,
});

const SectionStyles = breakpoints({
  base: css`
    
  `,
});

const ContentStyles = breakpoints({
  base: css`
    font-weight: 400;
    font-size: 1.5em;
    line-height: 1.3;
    font-family: ${theme.FONT('serif')};
  `,
});

const LabelStyles = breakpoints({
  base: css`
    padding-bottom: ${theme.UNIT(2)};
    color: ${theme.COLOR('mid')};
  `,
});

const DmsButtonStyles = breakpoints({
  base: css`

  `,
});

const IconLabelLinkStyles = breakpoints({
  base: css`
    color: ${theme.COLOR('mid')};
    .shp--icon-label {
      color: ${theme.COLOR('mid')};
      ${theme.FILL('mid')};

      &:hover {
        color: ${theme.COLOR('strong')};
        ${theme.FILL('strong')};
        ${TRANSITION('color', 'fill')};
      }
    }
  `,
});



// -------- Components -------->

const My = {
  Container: styled(AsideSection)<ContainerProps>`${ContainerStyles}`,
    Section: styled.div`${SectionStyles}`,
      Content: styled(ContentNode)`${ContentStyles}`,
      Label: styled(CellLabel)`${LabelStyles}`,
      DmsButton: styled(AsideMapButton)`${DmsButtonStyles}`,

      IconLabelLink: styled(IconLabelLink)`${IconLabelLinkStyles}`,

};

export const SliceAsideMapLink: Shapeable.FC<SliceAsideMapLinkProps> = (props) => {
  const { className, children, entity, zoom: propZoom } = props;

  const { content, link } = entity || {};

  const hasContent = !!content.text;
  const url = link?.url || link?.path;
  const hasLink = !!url;

  const activeEntity = usePageEntity() as EntityWithCoordinates;

  const { zoom } = activeEntity || {};

  const entityType = entityTypeNameFor(activeEntity);

  const entityLabel = activeEntity?.typeLabel || activeEntity._schema?.label || entityTypeNameFor(activeEntity);

  const dms = locationDMS(activeEntity);
  const t = useLang();
  
  const { showEntity } = useMapUtils();

  const dmsOnClick = () => {
    showEntity(activeEntity, {
      showMap: true,
      entityTypeNames: [entityType],
      select: true,
      zoom: zoom || propZoom,
    });
  };
  
  return (
    <My.Container colors={{ border: 'canvas' }} className={cls.name(className)} >
    {
      hasContent && (
      <My.Section>
        <My.Content entity={content} />
      </My.Section>
      )
    }
    {
      hasLink && (
      <My.Section>
        <My.IconLabelLink
          href={url}
          label={link?.label || link?.name}
        />
      </My.Section>
      )
    }
    {
      dms &&
      <My.Section>
        <My.DmsButton 
          onClick={dmsOnClick} 
          infoDescription={t(`This link will take you to an interactive map where you can explore the ${entityLabel} in more detail.`)}
          image={AUSTRALIA_MAP_THUMBNAIL}
          >
          {t('View on Map')}
        </My.DmsButton>
      </My.Section>
    }
    </My.Container>
  )
};

SliceAsideMapLink.defaultProps = SliceAsideMapLinkDefaultProps;
SliceAsideMapLink.cls = cls;